import { Component, Injectable, OnInit, OnDestroy, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs';
import { FormsModule } from '@angular/forms';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';

// Lista kierunkowych pickera wyciągnięta na zewnątrz komponentu, żeby formularze,
// które trzymają numer w bazie jako jeden string, mogły rozbić go na kierunkowy
// i resztę tą samą listą (patrz osoby kontaktowe w ustawieniach pacjenta).
// Tylko do odczytu — komponent nigdy nie modyfikuje elementów tej tablicy.
export const PHONE_PICKER_LANGS = [
  { lang: 'en', prefix: '+44' },
  { lang: 'pl', prefix: '+48' },
  { lang: 'de', prefix: '+49' },
  { lang: 'cs', prefix: '+420' },
  { lang: 'fr', prefix: '+33' },
  { lang: 'pt', prefix: '+351' },
  { lang: 'it', prefix: '+39' },
  { lang: 'ro', prefix: '+40' },
  { lang: 'da', prefix: '+45' },
  { lang: 'ar', prefix: '+966' },
  { lang: 'bg', prefix: '+359' },
  { lang: 'at', prefix: '+43' },
  { lang: 'be', prefix: '+32' },
  { lang: 'cy', prefix: '+357' },
  { lang: 'es', prefix: '+34' },
  { lang: 'et', prefix: '+372' },
  { lang: 'fi', prefix: '+358' },
  { lang: 'gr', prefix: '+30' },
  { lang: 'hu', prefix: '+36' },
  { lang: 'ie', prefix: '+353' },
  { lang: 'lu', prefix: '+352' },
  { lang: 'lt', prefix: '+370' },
  { lang: 'lv', prefix: '+371' },
  { lang: 'nl', prefix: '+31' },
  { lang: 'se', prefix: '+46' },
  { lang: 'sk', prefix: '+421' },
  { lang: 'sl', prefix: '+386' },
  { lang: 'is', prefix: '+354' },
  { lang: 'no', prefix: '+47' },
  { lang: 'hr', prefix: '+385' },
  { lang: 'al', prefix: '+355' },
  { lang: 'ch', prefix: '+41' },
  { lang: 'usa', prefix: '+1' },
  { lang: 'ua', prefix: '+380' },
  { lang: 'sr', prefix: '+381' },
  { lang: 'mt', prefix: '+356' },
];

// Rozbija zapisany numer (np. "+48123456789") na kierunkowy i resztę.
// Gdy numer nie zaczyna się od znanego kierunkowego, prefix zostaje pusty —
// picker podstawi wtedy domyślny kierunkowy z języka aplikacji.
export function splitPhoneByPrefix(stored: any): { prefix: string, number: string } {
  const value = String(stored == null ? '' : stored).replace(/[\s-]/g, '');
  if (value.charAt(0) === '+') {
    // Najdłuższe dopasowanie, żeby "+420" nie zostało zjedzone przez "+42"/"+4".
    const matched = PHONE_PICKER_LANGS
      .map(l => l.prefix)
      .filter(p => value.indexOf(p) === 0)
      .sort((a, b) => b.length - a.length)[0];
    if (matched) {
      return { prefix: matched, number: value.slice(matched.length) };
    }
  }
  return { prefix: null, number: value };
}

// Skleja kierunkowy z numerem z powrotem w jeden string do zapisu.
export function joinPhoneWithPrefix(prefix: any, number: any): string {
  const num = String(number == null ? '' : number).replace(/[\s-]/g, '');
  if (!num) { return ''; }
  // Numer wpisany od razu z kierunkowym zostawiamy bez zmian — inaczej powstałoby "+48+48...".
  if (num.charAt(0) === '+') { return num; }
  const pre = String(prefix == null ? '' : prefix).trim();
  return pre.charAt(0) === '+' ? pre + num : num;
}

@Component({
  selector: 'app-phone-picker',
  templateUrl: './phone-picker.component.html',
  styleUrls: ['./phone-picker.component.scss']
})

export class PhonePickerComponent implements OnInit, OnDestroy {

  @Input() prefixFormControl: FormControl;
  @Input() phoneFormControl: FormControl;
  @Input() height: number;
  @Input() onlyPrefix = false;
  @Input() onlyPhone = false;
  @Input() transparent = false;
  @Input() placeholder = '';
  /** Podgląd bez edycji: numer i prefiks widoczne, ale wyszarzone i nieedytowalne. */
  @Input() readonly = false;

  public langs = PHONE_PICKER_LANGS;
  public current;
  public searchTerm$ = new Subject<string>();
  public mask = "000000000000000";
  constructor(private translateService: TranslateService) { }

  ngOnInit() {

    this.searchTerm$
      .debounceTime(400)
      .distinctUntilChanged()
      .subscribe(searchKey => {
        this.onKeyChangeInput(searchKey);
      });

    if (!this.prefixFormControl.value) {
      this.prefixFormControl.setValue(this.translateService.currentLang);
    }

    const index = this.langs.findIndex(indx => indx.lang === this.prefixFormControl.value || indx.prefix === this.prefixFormControl.value);

    if (index !== -1) {
      this.changeAreaCode(this.langs[index]);
    } else {
      this.changeAreaCode({ lang: 'question', prefix: this.prefixFormControl.value });
    }

  }

  public changeAreaCode(lang): void {
    this.current = lang;
    this.prefixFormControl.setValue(this.current.prefix);
  }

  private onKeyChangeInput(searchValue): void {
    const index = this.langs.map(e => e.prefix).indexOf(searchValue);

    if (index !== -1) {
      this.changeAreaCode(this.langs[index]);
    } else {
      this.changeAreaCode({ lang: 'question', prefix: searchValue });
    }
  }

  ngOnDestroy() {
    if (this.searchTerm$) {
      this.searchTerm$.unsubscribe();
    }
  }

}
